import React, { useState } from 'react';
import { MessageCircle, ChevronDown, FileText, Briefcase, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface CandidateProfile {
  id: string;
  first_name?: string;
  last_name?: string;
  career_status?: string;
  base_locations?: string;
  preferred_locations?: string;
  role_preferences?: string;
  current_role?: string;
  current_level?: string;
}

interface CandidateInboxCardProps {
  candidateId: string;
  profile?: CandidateProfile;
  jobTitle: string;
  subject?: string;  // Subject line for privacy-friendly display
  timestamp: number;
  status: string;
  conversationId?: string;
  unreadCount: number;
  lastMessagePreview?: string;
  lastMessageSenderId?: string;
  currentUserId?: string;
  lastMessageAt?: number;
  isSelected: boolean;
  onClick: () => void;
  onStatusChange?: (newStatus: string) => void;
  onViewCV?: (candidateId: string) => void;  // Callback to open CV viewer
  salaryRange?: string;  // Salary range for recruiter reach-outs
  niceToKnow?: string;  // Optional additional info for recruiter reach-outs
}

const CandidateInboxCard: React.FC<CandidateInboxCardProps> = ({
  candidateId,
  profile,
  jobTitle,
  subject,
  timestamp,
  status,
  conversationId,
  unreadCount,
  lastMessagePreview,
  lastMessageSenderId,
  currentUserId,
  lastMessageAt,
  isSelected,
  onClick,
  onStatusChange,
  onViewCV,
  salaryRange,
  niceToKnow
}) => {
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const { t } = useTranslation('recruiterInbox');

  // Neutral B2B status meta — a single muted dot instead of emoji + rainbow chips.
  const statusOptions = [
    { value: 'candidate_sparked', label: t('statusLabels.candidate_sparked'), dot: 'bg-violet-400' },
    { value: 'in_talks', label: t('statusLabels.in_talks'), dot: 'bg-blue-400' },
    { value: 'initial_call_scheduled', label: t('statusLabels.initial_call_scheduled'), dot: 'bg-cyan-400' },
    { value: 'interviewing', label: t('statusLabels.interviewing'), dot: 'bg-amber-400' },
    { value: 'offer_talks', label: t('statusLabels.offer_talks'), dot: 'bg-orange-400' },
    { value: 'hired', label: t('statusLabels.hired'), dot: 'bg-emerald-500' },
    { value: 'currently_not_best_fit', label: t('statusLabels.currently_not_best_fit'), dot: 'bg-slate-400' }
  ];

  const currentStatus = statusOptions.find(s => s.value === status) || statusOptions[0];

  const handleStatusClick = (e: React.MouseEvent, newStatus: string) => {
    e.stopPropagation(); // Prevent card onClick
    if (onStatusChange) {
      onStatusChange(newStatus);
    }
    setShowStatusDropdown(false);
  };
  // Format career status
  const formatCareerStatus = (status?: string) => {
    if (!status) return 'Unknown Status';
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Format locations (truncate if too long)
  const formatLocations = (locations?: string) => {
    if (!locations) return 'Not specified';
    if (locations.length > 40) {
      return locations.substring(0, 40) + '...';
    }
    return locations;
  };

  // Get candidate name
  const getCandidateName = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name} ${profile.last_name}`;
    } else if (profile?.first_name) {
      return profile.first_name;
    }
    return candidateId.substring(0, 8) + '...';
  };

  // Format time
  const formatTime = (timestamp?: number) => {
    if (!timestamp) return '';
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div
      onClick={onClick}
      className={`group p-2.5 sm:p-3 cursor-pointer rounded-xl transition-colors border ${
        isSelected 
          ? 'bg-pink-50/50 border-pink-200 shadow-sm' 
          : unreadCount > 0 
            ? 'bg-white border-slate-200 shadow-sm hover:border-slate-300' 
            : 'bg-white border-slate-200 hover:border-slate-300'
      }`}
    >
      {/* Header Row: Status + Unread */}
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-1.5 min-w-0 flex-1">
          <span className="font-semibold text-xs sm:text-sm text-slate-900 truncate">
            {jobTitle || 'Reach-out'}
          </span>
          {unreadCount > 0 && (
            <span className="flex-shrink-0 bg-pink-500 text-white text-[8px] sm:text-[10px] font-semibold tabular-nums rounded-full w-3.5 h-3.5 sm:w-auto sm:h-auto sm:px-1.5 sm:min-w-[18px] flex items-center justify-center">
              {unreadCount}
            </span>
          )}
        </div>
        
        {/* Status Dropdown */}
        <div className="relative flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowStatusDropdown(!showStatusDropdown);
            }}
            className="flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium transition-colors border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
          >
            <span className={`size-1.5 rounded-full ${currentStatus.dot}`} />
            <span className="hidden sm:inline">{currentStatus.label}</span>
            <ChevronDown className="w-2.5 h-2.5 text-slate-400" />
          </button>
          
          {showStatusDropdown && (
            <div className="absolute right-0 mt-1 w-48 bg-white rounded-xl shadow-lg border border-slate-200 z-50 py-1 max-h-64 overflow-y-auto">
              {statusOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={(e) => handleStatusClick(e, option.value)}
                  className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-slate-50 transition-colors ${
                    option.value === status ? 'bg-slate-50 font-medium text-slate-900' : 'text-slate-600'
                  }`}
                >
                  <span className={`size-1.5 rounded-full ${option.dot}`} /> {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Subject (only if different from job title) */}
      {subject && subject !== jobTitle && (
        <div className="font-semibold text-xs sm:text-sm text-slate-800 truncate mb-1">
          {subject}
        </div>
      )}

      {/* Salary & Nice to Know */}
      {(salaryRange || niceToKnow) && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {salaryRange && (
            <span className="text-[10px] sm:text-xs text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">
              {salaryRange}
            </span>
          )}
          {niceToKnow && (
            <span className="text-[10px] sm:text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full truncate max-w-[180px]">
              {niceToKnow}
            </span>
          )}
        </div>
      )}

      {/* Candidate Fields - same layout as Interested Feed */}
      {profile && profile !== null && (profile.current_role || profile.base_locations || profile.role_preferences || profile.preferred_locations) && (
        <div className="space-y-1 mb-1.5 text-[10px] sm:text-xs">
          {(profile.current_role || profile.base_locations) && (
            <div className="flex items-start flex-wrap gap-x-3 gap-y-0.5 text-slate-500">
              {profile.current_role && (
                <span className="flex items-start gap-1">
                  <Briefcase className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  <span><span className="text-slate-400">{t('candidateCard.current')}</span> {profile.current_role}{profile.current_level ? ` (${profile.current_level})` : ''}</span>
                </span>
              )}
              {profile.base_locations && (
                <span className="flex items-start gap-1">
                  <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  <span>{formatLocations(profile.base_locations)}</span>
                </span>
              )}
            </div>
          )}
          {(profile.role_preferences || profile.preferred_locations) && (
            <p className="text-slate-400 line-clamp-1">
              <span className="text-slate-500 font-medium">{t('candidateCard.lookingFor')}</span>{' '}
              {[profile.role_preferences, profile.preferred_locations].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
      )}

      {/* View CV Button + Timestamp */}
      <div className="flex items-center justify-between mt-1.5">
        {onViewCV ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onViewCV(candidateId);
            }}
            className="flex items-center gap-1.5 px-2.5 py-1 text-[10px] sm:text-xs font-medium text-slate-600 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors border border-slate-200"
          >
            <FileText className="w-3 h-3" />
            {t('candidateCard.viewCV')}
          </button>
        ) : <div />}
        <span className="text-[10px] text-slate-400">
          {formatTime(lastMessageAt || timestamp)}
        </span>
      </div>

      {/* Loading skeleton */}
      {profile === undefined && (
        <div className="py-1.5">
          <div className="animate-pulse space-y-1.5">
            <div className="h-2.5 bg-slate-100 rounded w-3/4"></div>
            <div className="h-2.5 bg-slate-100 rounded w-full"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(CandidateInboxCard);
